import type { CellContext } from '@tanstack/react-table';
import { FileStack } from 'lucide-react';
import React, { useState, useEffect, memo, useCallback } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  PopoverArrow,
} from '@/components/ui/popover';
import type { IOneItemInPrepareForRequest } from '@/pages/PurchasesContainer/PurchasesRequests/api/Request.interface';
import { getDiscountStatusFn } from '@/pages/PurchasesContainer/RequestPerHand/utils/DiscountCheck';

import { getDiscountStats } from '../../utils/discountHelpers';

import DiscountMap from './DiscountMap';
import DiscountToolTipContent from './DiscountToolTipContent';
import DiscountDisplay from './DiscountValue';

// Extend the CellContext type to include your extra props
interface DiscountEditingCellProps
  extends CellContext<IOneItemInPrepareForRequest, unknown> {
  handleSelectItem: (item: IOneItemInPrepareForRequest) => void;
  handleShowCompetitors: (value: boolean) => void;
}

// Pre-defined styles to avoid recreation on each render
const STYLES = {
  container: 'flex flex-col items-center justify-between gap-1 px-1 py-1',
  valueContainer:
    'flex w-full min-w-16 items-center justify-between overflow-hidden rounded-md border hover:bg-gray-100 dark:hover:bg-gray-800',
  popoverContent: 'rounded-md border-gray-700 bg-background p-4 shadow-lg',
  input:
    'm-1 flex w-24 max-w-44 rounded-md border-2 border-blue-300 bg-white text-center text-3xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 disabled:bg-red-100 disabled:text-red-400 dark:border-blue-700 dark:bg-gray-900 dark:focus:border-blue-600 dark:focus:ring-blue-900/30 dark:disabled:bg-red-900/30 dark:disabled:text-red-300',
  offersButton:
    'mt-1 h-7 w-full border-blue-300 text-blue-700 text-xs py-0 px-2 shadow-sm hover:bg-blue-50 dark:border-blue-700 dark:text-blue-400 dark:hover:bg-blue-900/20',
  errorText: 'mx-1 mt-2 w-full rounded p-1 text-center text-xs text-danger',
};

const DiscountEditingCellComponent = ({
  getValue,
  row: { index, original },
  column: { id },
  table,
  handleSelectItem,
  handleShowCompetitors,
}: DiscountEditingCellProps) => {
  // Get the initial value for the cell.
  const initialValue = getValue();

  // Determine if the cell should be disabled - memoized calculation
  const isDisabled = !(
    original?.Qty > 0 &&
    original?.HealthyItemID &&
    Math.abs((original?.Price || 0) - (original?.healthyItemPrice || 0)) <= 0.5
  );

  const hasCompetitors = Boolean(original?.CompetitorsDiscounts?.length);

  const [isEdit, setIsEdit] = useState(false);

  // Local state for the cell's value.
  const [value, setValue] = useState<number>(Number(initialValue));
  // Local state for the total value.
  const [totalValue, setTotalValue] = useState<number>(
    Number(original?.TotalAfterDiscount),
  );

  // When the input loses focus, update the table data.
  const onBlur = useCallback(() => {
    const validValue = value > 0 && value <= 100 ? value : 0;
    table.options.meta?.updateData(index, id, validValue);

    const discounts = getDiscountStats(original?.CompetitorsDiscounts || []);
    const getDiscountStatus = getDiscountStatusFn(validValue, discounts);

    table.options.meta?.updateData(index, 'DiscountStatus', getDiscountStatus);
    table.options.meta?.updateData(index, 'TotalAfterDiscount', totalValue);
    setIsEdit(false);
  }, [
    value,
    totalValue,
    table.options.meta,
    index,
    id,
    original?.CompetitorsDiscounts,
  ]);

  // Keep local state in sync with external changes.
  useEffect(() => {
    setValue(Number(initialValue));
    setTotalValue(Number(original?.TotalAfterDiscount));
  }, [initialValue, original?.TotalAfterDiscount]);

  // Handle changes to the input.
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputVal = e.target.value;
      if (inputVal === '') {
        setValue(0);
        return;
      }

      // For DiscountEdit, we only allow values between 0 and 100.
      const newValue = Number(inputVal);
      if (newValue < 0 || newValue > 100) return;

      setValue(newValue);
      setTotalValue(
        (original?.healthyItemPrice || 0) *
          original?.Qty *
          ((100 - newValue) / 100),
      );
    },
    [original?.healthyItemPrice, original?.Qty],
  );

  // Show all competitors button handler - memoized
  const handleShowAllCompetitors = useCallback(() => {
    if (hasCompetitors) {
      handleSelectItem(original);
      handleShowCompetitors(true);
    }
  }, [hasCompetitors, handleSelectItem, handleShowCompetitors, original]);

  // Memoized discount editing UI
  const renderEditMode = useCallback(
    () => (
      <div className="flex justify-center">
        <Input
          disabled={isDisabled}
          className={STYLES.input}
          type="number"
          value={value > 0 ? value : ''}
          onChange={handleChange}
          onBlur={onBlur}
          autoFocus
        />
      </div>
    ),
    [isDisabled, value, handleChange, onBlur],
  );

  // Memoized discount display UI
  const renderDisplayMode = useCallback(
    () => (
      <div className={STYLES.container}>
        <Popover>
          <PopoverTrigger className="w-full">
            <div
              className={`${STYLES.valueContainer} ${!isDisabled ? 'cursor-pointer' : ''}`}
            >
              <DiscountMap discountStatus={original?.DiscountStatus} />
              <DiscountDisplay
                discount={original?.Discount}
                discountEdit={original?.DiscountEdit}
              />
            </div>
          </PopoverTrigger>
          <PopoverContent className={STYLES.popoverContent}>
            <PopoverArrow />
            <DiscountToolTipContent
              discountStatus={original?.DiscountStatus}
              competitorsCount={original?.CompetitorsDiscounts?.length || 0}
              maxDiscount={original?.bestDiscount || 0}
              minDiscount={original?.minDiscount || 0}
            />
            <div className="mt-3 flex justify-end">
              <Button
                disabled={isDisabled}
                onClick={() => {
                  if (!isDisabled) {
                    setIsEdit(true);
                  }
                }}
                variant="destructive"
                size="sm"
              >
                تعديل الخصم
              </Button>
            </div>
            {isDisabled && (
              <div className={STYLES.errorText}>
                لتعديل الخصم لابد من اضافة الكمية اولا
              </div>
            )}
            <PopoverArrow className="fill-primary" />
          </PopoverContent>
        </Popover>

        {/* Compact button for all offers in column layout */}
        {hasCompetitors && (
          <Button
            disabled={!hasCompetitors}
            className={STYLES.offersButton}
            variant="outline"
            size="sm"
            onClick={handleShowAllCompetitors}
          >
            <FileStack className="mr-1 h-3 w-3" />
            <span className="text-[10px]">كل العروض</span>
          </Button>
        )}
      </div>
    ),
    [isDisabled, original, hasCompetitors, handleShowAllCompetitors],
  );

  return isEdit ? renderEditMode() : renderDisplayMode();
};

// Memoize the component to prevent unnecessary re-renders
const DiscountEditingCell = memo(DiscountEditingCellComponent);

export default DiscountEditingCell;
